import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

const routeTitleFallbacks = [
  { match: (pathname) => pathname === '/', title: 'Bài thi', parent: 'Thư viện bài thi' },
  { match: (pathname) => pathname === '/change-password', title: 'Đổi mật khẩu', parent: 'Người dùng' },
  { match: (pathname) => pathname === '/profile', title: 'Trang cá nhân', parent: 'Người dùng' },
  { match: (pathname) => pathname === '/direct-mock-results', title: 'Kết quả thi thử', parent: 'Người dùng' },
  { match: (pathname) => pathname === '/history', title: 'Lịch sử', parent: 'Người dùng' },
  { match: (pathname) => pathname === '/class', title: 'Lớp học', parent: 'Người dùng' },
  { match: (pathname) => pathname.startsWith('/class-detail/'), title: 'Chi tiết lớp học', parent: 'Người dùng' },
  { match: (pathname) => pathname.startsWith('/exam-detail/'), title: 'Chi tiết bài thi', parent: 'Thư viện bài thi' },
  { match: (pathname) => pathname.startsWith('/exam-test/'), title: 'Làm bài thi', parent: 'Thư viện bài thi' },
  { match: (pathname) => pathname.startsWith('/part-test/'), title: 'Làm bài luyện tập', parent: 'Thư viện bài thi' },
  { match: (pathname) => pathname.startsWith('/time-up/'), title: 'Hết giờ', parent: 'Thư viện bài thi' },
  { match: (pathname) => pathname.startsWith('/write-test-review/'), title: 'Chữa bài Writing', parent: 'Người dùng' }
];

const findRouteFallback = (pathname) => routeTitleFallbacks.find((route) => route.match(pathname));

export default function Breadcrumbs({ navigation }) {
  const theme = useTheme();
  const location = useLocation();

  const activeMenu = useMemo(() => {
    const findActiveItem = (menu) => {
      if (!menu.children) return null;

      for (const collapse of menu.children) {
        if (collapse.type === 'collapse') {
          const nestedItem = findActiveItem(collapse);
          if (nestedItem) return nestedItem;
        }

        if (collapse.type === 'item' && location.pathname === collapse.url) {
          return { main: menu, item: collapse };
        }
      }

      return null;
    };

    for (const menu of navigation?.items || []) {
      if (menu.type === 'group') {
        const activeItem = findActiveItem(menu);
        if (activeItem) return activeItem;
      }
    }

    return {};
  }, [location.pathname, navigation]);

  const fallback = useMemo(() => findRouteFallback(location.pathname), [location.pathname]);
  const pageTitle = fallback?.title || activeMenu.item?.title || 'Trang';
  const parentTitle = fallback?.parent || activeMenu.main?.title;

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 }, pb: { xs: 0.75, sm: 1 }, bgcolor: theme.palette.background.default }}>
      <Typography
        component="h1"
        variant="h3"
        sx={{
          mb: 0.75,
          fontWeight: 700,
          lineHeight: 1.2,
          color: 'text.primary'
        }}
      >
        {pageTitle}
      </Typography>
      <MuiBreadcrumbs
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.95rem'
          },
          '& .MuiBreadcrumbs-separator': {
            mx: 1.25,
            color: theme.palette.divider
          }
        }}
      >
        <Typography
          component={Link}
          to="/"
          sx={{
            textDecoration: 'none',
            color: theme.palette.text.secondary,
            fontWeight: 500,
            transition: theme.transitions.create(['color'], {
              duration: theme.transitions.duration.short
            }),
            '&:hover': {
              color: theme.palette.primary.main
            }
          }}
        >
          Trang chủ
        </Typography>
        {parentTitle && (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500
            }}
          >
            {parentTitle}
          </Typography>
        )}
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600
          }}
        >
          {pageTitle}
        </Typography>
      </MuiBreadcrumbs>
    </Box>
  );
}

Breadcrumbs.propTypes = {
  navigation: PropTypes.object
};
